import React, { createContext, useContext, useEffect, useState } from 'react';
import { SelectionCounts } from 'Compass/profile-lab/types';
import { db } from 'Compass/profile-lab/services/db';

interface SelectionContextType extends SelectionCounts {
  refreshCounts: () => Promise<void>;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
  processingStartTime: number | null;
  processingStatus: string;
  setProcessingStatus: (status: string) => void;
}

const SelectionContext = createContext<SelectionContextType | undefined>(undefined);

export const SelectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [counts, setCounts] = useState<SelectionCounts>({
    growCount: 0,
    selectedCount: 0,
    totalCount: 0,
  });
  const [isProcessing, setIsProcessingState] = useState(false);
  const [processingStartTime, setProcessingStartTime] = useState<number | null>(null);
  const [processingStatus, setProcessingStatus] = useState('');

  const setIsProcessing = (processing: boolean) => {
    setIsProcessingState(processing);
    setProcessingStartTime(processing ? Date.now() : null);
    if (processing) {
      setProcessingStatus('');
    }
  };

  const fetchCounts = async () => {
    try {
      await db.init();

      const selectionState = await db.getSelectionState();
      const totalCount = await db.getPhotosCount();

      setCounts({
        growCount: selectionState.grow.length,
        selectedCount: selectionState.selected.length,
        totalCount: totalCount,
      });
    } catch (error) {
      console.error('Failed to fetch selection counts from IndexedDB:', error);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        await db.init();
        await fetchCounts();
      } catch (error) {
        console.error('Error initializing Profile Lab:', error);
        throw error;
      }
    };

    initializeData().catch((error) => {
      console.error('Failed to initialize Profile Lab:', error);
    });
  }, []);

  return (
    <SelectionContext.Provider value={{ ...counts, refreshCounts: fetchCounts, isProcessing, setIsProcessing, processingStartTime, processingStatus, setProcessingStatus }}>
      {children}
    </SelectionContext.Provider>
  );
};

export const useSelectionContext = (): SelectionContextType => {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error('useSelectionContext must be used within a SelectionProvider');
  }
  return context;
};
